import type { ReactNode } from 'react';
import { AlertTriangle, FileText, FileX, Loader2 } from 'lucide-react';
import { Badge } from '@hub/ui';
import { useCaseDocument } from '../sources/caseDocument';
import { PdfDocumentViewer } from './pdfDocumentViewer';

/**
 * The source-document pane: navy record header, PDF.js viewer, status footer.
 *
 * Shared by the in-view compare pane and the popped-out document window so
 * there is exactly ONE place that decides which document a case shows. They
 * previously each rendered the bundled synthetic sample unconditionally, and
 * fixing that in two files invites the next slice to fix it in one.
 *
 * The document comes from useCaseDocument, which resolves an empty
 * documentBlobId to `none` and never falls back to the sample — see that
 * module for why that fallback is the failure this pane is designed around.
 */
export function CaseDocumentPane({
  documentBlobId,
  mrn,
  procedure,
  headerAction,
  footerSuffix,
  onPopOut,
  className,
}: {
  documentBlobId: string;
  mrn: string;
  /** Procedure text for the footer. Empty on live cases until the FA leg lands. */
  procedure?: string;
  /** Header-right control — "Pop out" in-view, "Return to review" in the window. */
  headerAction?: ReactNode;
  /** Extra footer text appended after the document status. */
  footerSuffix?: string;
  /** Offered by the viewer's own render-error state. Omit inside the pop-out. */
  onPopOut?: () => void;
  className?: string;
}) {
  const doc = useCaseDocument(documentBlobId);

  return (
    <div className={className}>
      <div className="flex items-center gap-2 border-b border-border bg-coronis-navy-700 px-3 py-2.5 text-white">
        <FileText className="size-4 shrink-0 text-coronis-blue-200" />
        <span className="text-[13px] font-semibold">Anesthesia record</span>
        {mrn && <span className="font-mono text-[11.5px] text-coronis-blue-200">{mrn}</span>}
        {/* The badge marks the synthetic record. It is not the safety
            mechanism — the sentinel in useCaseDocument is — but a real
            document must never wear it, or the reviewer learns to ignore it. */}
        {doc.status === 'ready' && doc.isSample && (
          <Badge className="ml-1 border-coronis-amber-300 bg-coronis-amber-100 text-[10px] font-semibold uppercase tracking-wide text-coronis-amber-700">
            Sample
          </Badge>
        )}
        {headerAction && <span className="ml-auto flex items-center">{headerAction}</span>}
      </div>

      {doc.status === 'ready' ? (
        <PdfDocumentViewer
          url={doc.url}
          onPopOut={onPopOut}
          title={
            doc.isSample
              ? `Anesthesia record ${mrn} (sample)`
              : `Anesthesia record ${mrn}`.trim()
          }
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-muted/40 px-6 py-10 text-center">
          {doc.status === 'loading' && (
            <>
              <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
              <span className="text-[12.5px] text-muted-foreground" aria-busy="true">
                Loading document…
              </span>
            </>
          )}
          {doc.status === 'none' && (
            <>
              <FileX className="size-6 text-muted-foreground" aria-hidden />
              <span className="text-[13px] font-semibold text-foreground">
                No document for this case
              </span>
              {/* Say plainly that there is nothing to compare against. A
                  reviewer who cannot see the record cannot adjudicate the
                  codes, and should route the case rather than guess. */}
              <span className="max-w-[42ch] text-[12px] text-muted-foreground">
                No source anesthesia record is stored for this case, so the coded
                results cannot be checked against the document. Route the case as
                missing information rather than adjudicating it.
              </span>
            </>
          )}
          {doc.status === 'error' && (
            <>
              <AlertTriangle className="size-6 text-coronis-amber-500" aria-hidden />
              <span className="text-[13px] font-semibold text-foreground">
                The source document couldn’t be loaded
              </span>
              <span className="max-w-[42ch] text-[12px] text-muted-foreground">
                This case has a stored record but it could not be retrieved. Try
                again; if it keeps failing, route the case rather than
                adjudicating it without the document.
              </span>
            </>
          )}
        </div>
      )}

      <div className="border-t border-border bg-card px-3 py-1.5 text-[11px] text-muted-foreground">
        {footerNote(doc.status, doc.status === 'ready' && doc.isSample, procedure)}
        {footerSuffix ? ` · ${footerSuffix}` : ''}
      </div>
    </div>
  );
}

/** Footer status line. Only the sample may be described as synthetic. */
function footerNote(
  status: 'none' | 'loading' | 'ready' | 'error',
  isSample: boolean,
  procedure?: string,
): string {
  const proc = procedure ? ` — ${procedure}` : '';
  switch (status) {
    case 'none':
      return 'No source document stored for this case';
    case 'loading':
      return 'Loading source document…';
    case 'error':
      return 'Source document unavailable';
    case 'ready':
      return isSample ? `Synthetic sample document${proc}` : `Source anesthesia record${proc}`;
  }
}
